import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, Download, CheckCircle2, XCircle, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

export function CargaMasiva() {
  var stateEntity = useState('');
  var selectedEntity = stateEntity[0];
  var setSelectedEntity = stateEntity[1];
  
  var stateFile = useState(null);
  var file = stateFile[0];
  var setFile = stateFile[1];
  
  var stateUploading = useState(false);
  var uploading = stateUploading[0];
  var setUploading = stateUploading[1];
  
  var stateResultado = useState(null);
  var resultado = stateResultado[0];
  var setResultado = stateResultado[1];

  function descargarPlantilla() {
    if (!selectedEntity) {
      toast.error('Selecciona una entidad');
      return;
    }
    axios.get(API + '/carga-masiva/plantilla/' + selectedEntity, { responseType: 'blob' })
      .then(function(response) {
        var url = window.URL.createObjectURL(new Blob([response.data]));
        var link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'plantilla_' + selectedEntity + '.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Plantilla descargada');
      })
      .catch(function() {
        toast.error('Error al descargar');
      });
  }

  function handleFileChange(e) {
    var f = e.target.files[0];
    if (f) {
      var name = f.name.toLowerCase();
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        setFile(f);
        setResultado(null);
      } else {
        toast.error('Solo archivos Excel');
      }
    }
  }

  function handleUpload() {
    if (!selectedEntity || !file) {
      toast.error('Selecciona entidad y archivo');
      return;
    }
    setUploading(true);
    setResultado(null);

    var formData = new FormData();
    formData.append('file', file);

    var url = selectedEntity === 'dotacion' 
      ? API + '/carga-dotacion'
      : API + '/carga-masiva/' + selectedEntity;

    axios.post(url, formData)
      .then(function(res) {
        setResultado(res.data);
        setUploading(false);
        if (selectedEntity === 'dotacion') {
          var msgs = [];
          if (res.data.personas_creadas > 0) msgs.push(res.data.personas_creadas + ' personas creadas');
          if (res.data.personas_actualizadas > 0) msgs.push(res.data.personas_actualizadas + ' actualizadas');
          if (res.data.gerencias_creadas > 0) msgs.push(res.data.gerencias_creadas + ' gerencias');
          if (res.data.superintendencias_creadas > 0) msgs.push(res.data.superintendencias_creadas + ' superintendencias');
          if (res.data.cargos_creados > 0) msgs.push(res.data.cargos_creados + ' cargos');
          if (res.data.jerarquia_actualizada > 0) msgs.push(res.data.jerarquia_actualizada + ' relaciones jerarquia');
          if (msgs.length > 0) toast.success(msgs.join(', '));
        } else {
          var msgs2 = [];
          if (res.data.exitosos > 0) msgs2.push(res.data.exitosos + ' creados');
          if (res.data.actualizados > 0) msgs2.push(res.data.actualizados + ' actualizados');
          if (msgs2.length > 0) toast.success(msgs2.join(', '));
        }
        if (res.data.errores && res.data.errores.length > 0) toast.warning(res.data.errores.length + ' errores');
      })
      .catch(function(err) {
        setUploading(false);
        var msg = 'Error al procesar el archivo';
        if (err.response && err.response.data) {
          if (err.response.data.detail) {
            msg = err.response.data.detail;
          } else if (typeof err.response.data === 'string') {
            msg = err.response.data;
          }
        } else if (err.message) {
          msg = err.message;
        }
        toast.error(msg);
        setResultado({ exitosos: 0, actualizados: 0, errores: [msg], total: 0 });
      });
  }

  function handleEntityChange(e) {
    setSelectedEntity(e.target.value);
    setFile(null);
    setResultado(null);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Carga Masiva</h1>
        <p className="text-slate-500">Importar datos desde archivos Excel</p>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Instrucciones:</p>
            <p>1. Selecciona la entidad y descarga la plantilla</p>
            <p>2. Completa los datos en Excel siguiendo el formato</p>
            <p>3. Sube el archivo y revisa los resultados</p>
            <p className="mt-2 text-blue-600">Orden: Áreas → Cargos → Personas → Riesgos → Controles</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>1. Selecciona la entidad</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={selectedEntity} onChange={handleEntityChange} className="flex-1 p-2 border rounded-lg">
              <option value="">-- Seleccionar --</option>
              <option value="dotacion">Dotacion de Personal (completa)</option>
              <option value="areas">Areas</option>
              <option value="cargos">Cargos</option>
              <option value="personas">Personas</option>
              <option value="actividades">Actividades</option>
              <option value="riesgos">Riesgos</option>
              <option value="controles">Controles</option>
            </select>
            <Button onClick={descargarPlantilla} variant="outline" disabled={!selectedEntity || selectedEntity === 'dotacion'} className="border-teal-300 text-teal-700">
              <Download className="w-4 h-4 mr-2" />
              Descargar Plantilla
            </Button>
          </div>
          {selectedEntity === 'dotacion' && (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
              <p className="font-medium mb-1">Carga de Dotacion de Personal</p>
              <p>Sube tu archivo Excel con las columnas: RUT, NOMBRE, CARGO, FECHA INGRESO, SEXO, GERENCIA, SUPERINTENDENCIA, FECHA NACIMIENTO, NACIONALIDAD, CARGO JEFE, JORNADA</p>
              <p className="mt-1 text-teal-600">Se crearan automaticamente: Gerencias, Superintendencias y Cargos (con jerarquia).</p>
            </div>
          )}
        </CardContent>
      </Card>

      <UploadSection 
        show={selectedEntity !== ''} 
        file={file} 
        uploading={uploading}
        onFileChange={handleFileChange}
        onUpload={handleUpload}
      />

      <ResultsSection resultado={resultado} />
    </div>
  );
}

function UploadSection(props) {
  if (!props.show) return null;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>2. Sube tu archivo</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center mb-4">
          <input type="file" id="file-input" accept=".xlsx,.xls" onChange={props.onFileChange} className="hidden" />
          <label htmlFor="file-input" className="cursor-pointer">
            <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700">{props.file ? props.file.name : 'Clic para seleccionar Excel'}</p>
          </label>
        </div>
        {props.file && (
          <Button onClick={props.onUpload} disabled={props.uploading} className="w-full bg-teal-500 hover:bg-teal-600">
            {props.uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {props.uploading ? 'Procesando...' : 'Cargar Datos'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ResultsSection(props) {
  var resultado = props.resultado;
  if (!resultado) return null;

  var errores = resultado.errores || [];
  var areasCreadas = resultado.areas_creadas || [];
  var cargosCreados = resultado.cargos_creados || [];
  
  // Support both regular and dotacion formats
  var isDotacion = resultado.personas_creadas !== undefined;
  var creados = isDotacion ? resultado.personas_creadas : (resultado.exitosos || 0);
  var actualizados = isDotacion ? resultado.personas_actualizadas : (resultado.actualizados || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>3. Resultados</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold">{resultado.total}</p>
            <p className="text-sm text-slate-500">Total filas</p>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <p className="text-2xl font-bold text-emerald-600">{creados}</p>
            <p className="text-sm text-emerald-600">{isDotacion ? 'Personas creadas' : 'Creados'}</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{actualizados}</p>
            <p className="text-sm text-blue-600">{isDotacion ? 'Personas actualizadas' : 'Actualizados'}</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{errores.length}</p>
            <p className="text-sm text-red-600">Errores</p>
          </div>
        </div>
        
        {isDotacion && (resultado.gerencias_creadas > 0 || resultado.superintendencias_creadas > 0 || resultado.cargos_creados > 0 || resultado.jerarquia_actualizada > 0) && (
          <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg mb-3">
            <p className="text-sm font-medium text-teal-800 mb-1">Dependencias creadas automaticamente:</p>
            <div className="flex flex-wrap gap-3 text-sm text-teal-700">
              {resultado.gerencias_creadas > 0 && <span>{resultado.gerencias_creadas} gerencia(s)</span>}
              {resultado.superintendencias_creadas > 0 && <span>{resultado.superintendencias_creadas} superintendencia(s)</span>}
              {resultado.cargos_creados > 0 && <span>{resultado.cargos_creados} cargo(s)</span>}
              {resultado.jerarquia_actualizada > 0 && <span>{resultado.jerarquia_actualizada} relaciones de jerarquia</span>}
            </div>
          </div>
        )}
        
        <SuccessMessage count={creados} actualizados={actualizados} />
        <AutoCreatedItems areasCreadas={areasCreadas} cargosCreados={cargosCreados} />
        <ErrorsList errores={errores} />
      </CardContent>
    </Card>
  );
}

function SuccessMessage(props) {
  if (props.count <= 0 && (!props.actualizados || props.actualizados <= 0)) return null;
  return (
    <div className="space-y-2 mb-3">
      {props.count > 0 && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <span className="text-emerald-700">{props.count} registros creados</span>
        </div>
      )}
      {props.actualizados > 0 && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-blue-600" />
          <span className="text-blue-700">{props.actualizados} registros actualizados</span>
        </div>
      )}
    </div>
  );
}

function AutoCreatedItems(props) {
  var areasCreadas = props.areasCreadas || [];
  var cargosCreados = props.cargosCreados || [];
  
  if (areasCreadas.length === 0 && cargosCreados.length === 0) return null;
  
  return (
    <div className="p-3 bg-blue-50 rounded-lg mb-3">
      <p className="text-sm font-medium text-blue-800 mb-2">Creados automáticamente:</p>
      {areasCreadas.length > 0 && (
        <p className="text-sm text-blue-700">
          <span className="font-medium">Áreas:</span> {areasCreadas.join(', ')}
        </p>
      )}
      {cargosCreados.length > 0 && (
        <p className="text-sm text-blue-700">
          <span className="font-medium">Cargos:</span> {cargosCreados.join(', ')}
        </p>
      )}
    </div>
  );
}

function ErrorsList(props) {
  var errores = props.errores;
  if (!errores || errores.length === 0) return null;
  
  var items = [];
  for (var i = 0; i < errores.length; i++) {
    items.push(
      <div key={i} className="flex items-start gap-2 py-1 text-sm text-red-700">
        <XCircle className="w-4 h-4 mt-0.5" />
        <span>{errores[i]}</span>
      </div>
    );
  }
  
  return (
    <div className="bg-red-50 rounded-lg p-3 max-h-40 overflow-y-auto">
      {items}
    </div>
  );
}
